/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, Save } from "lucide-react";
import { apiFetch } from "../auth/apiClient";
import { goodsApi } from "./api";
import "./GoodsForm.css";

const CONDITIONS = ["new", "like_new", "good", "fair"];
const CATEGORIES = ["Electronics", "Furniture", "Books", "Clothing", "Kitchen", "Sports", "Other"];

export default function GoodsForm() {
  const { id } = useParams();  // undefined = create
  const isEdit = !!id;
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("");
  const [condition, setCondition] = useState("");
  const [photos, setPhotos] = useState([]);  // [{id, sort_order}]
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    goodsApi.get(id).then((item) => {
      setTitle(item.title || "");
      setDescription(item.description || "");
      setPrice(item.price != null ? String(item.price) : "");
      setCategory(item.category || "");
      setCondition(item.condition || "");
      setPhotos(item.photos || []);
      setLoading(false);
    }).catch((e) => {
      setError(e.message || "Could not load listing");
      setLoading(false);
    });
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError("Title is required"); return; }
    setSaving(true);
    setError("");
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || undefined,
        price: price !== "" ? parseFloat(price) : undefined,
        category: category || undefined,
        condition: condition || undefined,
      };
      if (isEdit) {
        await goodsApi.patch(id, body);
        navigate(`/goods/${id}`);
      } else {
        const item = await goodsApi.create(body);
        navigate(`/goods/${item.id}`);
      }
    } catch (e) {
      setError(e.message || "Could not save listing");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = useCallback(async (file) => {
    if (!id) { setError("Save the listing first before adding photos"); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`/api/goods/${id}/photos`, {
        method: "POST",
        body: fd,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: "Upload failed" }));
        throw new Error(err.detail || "Upload failed");
      }
      const photo = await resp.json();
      setPhotos((prev) => [...prev, photo]);
    } catch (e) {
      setError(e.message || "Could not upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  }, [id]);

  if (loading) return <div className="gdf-loading">Loading…</div>;

  return (
    <div className="gdf-page">
      <div className="gdf-back-bar">
        <button className="gdf-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </button>
      </div>
      <h1 className="gdf-heading">{isEdit ? "Edit Listing" : "List an Item"}</h1>
      {error && <p className="gdf-error">{error}</p>}

      <form className="gdf-form" onSubmit={handleSubmit}>
        <label className="gdf-label">
          Title <span className="gdf-req">*</span>
          <input
            className="gdf-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Sony 55 inch TV"
          />
        </label>

        <div className="gdf-row">
          <label className="gdf-label gdf-half">
            Price (₹) — leave blank if free
            <input
              className="gdf-input"
              type="number"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="5000"
            />
          </label>
          <label className="gdf-label gdf-half">
            Category
            <select
              className="gdf-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">— Select —</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>

        <label className="gdf-label">
          Condition
          <select
            className="gdf-select"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          >
            <option value="">— Select —</option>
            {CONDITIONS.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
        </label>

        <label className="gdf-label">
          Description
          <textarea
            className="gdf-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What are you selling? Include condition details, reason for selling, etc."
            rows={4}
          />
        </label>

        {isEdit && (
          <div className="gdf-label">
            Photos (up to 6)
            <div className="gdf-photos-row">
              {photos.map((p) => (
                <img
                  key={p.id}
                  className="gdf-photo-thumb"
                  src={goodsApi.photoUrl(id, p.id)}
                  alt="listing"
                />
              ))}
              {photos.length < 6 && (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handlePhotoUpload(f);
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    className="gdf-add-photo"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadingPhoto}
                  >
                    <ImagePlus size={20} />
                    {uploadingPhoto ? "…" : "Add photo"}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <button className="gdf-submit" type="submit" disabled={saving}>
          <Save size={16} />
          {saving ? "Saving…" : isEdit ? "Save changes" : "List item"}
        </button>
      </form>
    </div>
  );
}
